import type { FeaturePrototype } from '@puppycad/cad-core';
import type { ExtrudeViewportRef } from './extrude-types';

/**
 * 特征提交结果中可用于增量同步的网格数据。
 */
type CommitMeshData = {
  /** 结果体对象 ID。 */
  id?: unknown;
  /** 结果体元数据。 */
  metadata?: Record<string, unknown>;
};

/**
 * 特征提交后的最小增量同步结果。
 */
export type FeatureCommitMeshDeltaResult = {
  /** 是否替换既有特征。 */
  replaced: boolean;
  /** 特征 ID。 */
  featureId: string;
  /** 特征原型。 */
  prototype?: FeaturePrototype | null;
  /** 主结果体网格。 */
  meshData?: unknown | null;
  /** 全部结果体网格。 */
  meshDataList?: unknown[];
  /** 主结果体 ID。 */
  objectId?: string;
  /** 全部结果体 ID。 */
  objectIds?: string[];
};

/**
 * 判断值是否为非空字符串。
 *
 * @param value 待判断值。
 * @returns 是否为非空字符串。
 */
function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * 从未知数组中提取字符串 ID。
 *
 * @param value 待解析值。
 * @returns 字符串 ID 集合。
 */
function readStringIdArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isNonEmptyString);
}

/**
 * 读取网格数据对象 ID。
 *
 * @param meshData 网格数据。
 * @returns 网格 ID；缺失时返回 null。
 */
function readMeshDataId(meshData: unknown): string | null {
  const id = (meshData as CommitMeshData | null)?.id;
  return isNonEmptyString(id) ? id : null;
}

/**
 * 读取提交结果中的正式结果体网格列表。
 *
 * @param result 提交结果。
 * @returns 网格数据列表。
 */
function readCommittedMeshDataList(result: FeatureCommitMeshDeltaResult): unknown[] {
  if (Array.isArray(result.meshDataList) && result.meshDataList.length > 0) {
    return result.meshDataList.filter(Boolean);
  }
  return result.meshData ? [result.meshData] : [];
}

/**
 * 从结果体元数据中收集本次提交会替换或吸收的旧实体 ID。
 *
 * 设计说明：
 * 1. 合并凸台会删除被吸收实体，底层通过 `mergedSourceIds` 标记；
 * 2. 切除会删除原目标体并写入新体，底层通过 `sourceTargetId/deletedSolidIds` 标记；
 * 3. 基于面创建特征时执行器会移除来源面实体，因此这里也按 `sourceType=face + sourceId` 回收旧体。
 *
 * @param meshData 正式结果体网格。
 * @param outputIds 本次提交生成的结果体 ID。
 * @returns 需要移除的旧实体 ID。
 */
function collectRemovedSourceIdsFromMeshData(meshData: unknown, outputIds: ReadonlySet<string>): string[] {
  const metadata = (meshData as CommitMeshData | null)?.metadata ?? {};
  const removedIds = new Set<string>();
  readStringIdArray(metadata.mergedSourceIds).forEach((id) => removedIds.add(id));
  readStringIdArray(metadata.deletedSolidIds).forEach((id) => removedIds.add(id));
  if (isNonEmptyString(metadata.sourceTargetId)) {
    removedIds.add(metadata.sourceTargetId);
  }
  if (metadata.sourceType === 'face' && isNonEmptyString(metadata.sourceId)) {
    removedIds.add(metadata.sourceId);
  }
  outputIds.forEach((id) => removedIds.delete(id));
  return [...removedIds];
}

/**
 * 将特征提交结果按最小范围同步到视口。
 *
 * 设计说明：
 * 提交结果已经携带新结果体 mesh，前端不应再通过全量 `syncFeatureMeshes` 重建整个场景；
 * 该函数只移除底层明确删除的旧实体，并添加或替换本次提交生成的结果体。
 *
 * @param viewportRef 视口适配器引用。
 * @param result 提交结果。
 */
export async function applyFeatureCommitMeshDelta(
  viewportRef: ExtrudeViewportRef,
  result: FeatureCommitMeshDeltaResult,
): Promise<void> {
  const meshDataList = readCommittedMeshDataList(result);
  if (meshDataList.length === 0) {
    throw new Error(`特征提交缺少结果体网格，无法执行增量同步(featureId=${result.featureId})`);
  }

  const outputIds = new Set([
    ...readStringIdArray(result.objectIds),
    ...(isNonEmptyString(result.objectId) ? [result.objectId] : []),
    ...meshDataList.map(readMeshDataId).filter(isNonEmptyString),
  ]);
  const removedIds = new Set<string>();
  meshDataList.forEach((meshData) => {
    collectRemovedSourceIdsFromMeshData(meshData, outputIds).forEach((id) => removedIds.add(id));
  });

  removedIds.forEach((meshId) => {
    viewportRef.value?.removeMesh(meshId);
  });

  for (const meshData of meshDataList) {
    await viewportRef.value?.addMesh(meshData, {
      prototype: result.prototype ?? null,
    });
  }
}
